import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Lock, RefreshCw, Download, Filter } from 'lucide-react';
import { Button } from '../Button';
import { toast } from 'sonner';
import { FilterSheet, FilterChips, DEFAULT_FILTERS, countActive, type FilterState } from './FilterSheet';
export function PageHeader({
  group,
  title,
  description,
  icon: Icon,
  onFilter,
  onRefresh,
  onExport,
  onFilterChange,
  extraActions,
  lastUpdated = '2 menit lalu',
  classification = 'RAHASIA',
  filters: filtersProp
}: {
  group?: string;
  title: string;
  description?: string;
  icon?: ComponentType<{
    className?: string;
  }>;
  onFilter?: () => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onFilterChange?: (next: FilterState) => void;
  extraActions?: React.ReactNode;
  lastUpdated?: string;
  classification?: string;
  filters?: FilterState;
}) {
  const [internalFilters, setInternalFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const filters = filtersProp ?? internalFilters;
  const setFilters = (next: FilterState) => {
    if (!filtersProp) setInternalFilters(next);
    onFilterChange?.(next);
  };
  const activeCount = countActive(filters);
  const defaultRefresh = () => toast.info('Memuat ulang data...', {
    description: 'Data diperbarui dari sumber resmi.'
  });
  const defaultExport = () => toast.success('Ekspor laporan berhasil', {
    description: 'File PDF telah disiapkan untuk diunduh.'
  });
  const handleFilterClick = () => {
    if (onFilter) return onFilter();
    setFilterOpen(true);
  };
  const handleApply = (next: FilterState) => {
    setFilters(next);
    const count = countActive(next);
    if (count > 0) {
      toast.success(`${count} filter diterapkan`, {
        description: 'Data sedang dimuat ulang dengan filter terbaru.'
      });
    } else {
      toast.info('Semua filter direset');
    }
  };
  const handleClearChip = (key: keyof FilterState, value?: string) => {
    const next: FilterState = {
      ...filters
    };
    if (key === 'keyword') next.keyword = '';else if (key === 'timeRange') next.timeRange = '24h';else if (Array.isArray((next as any)[key])) {
      ;
      (next as any)[key] = ((next as any)[key] as string[]).filter(x => x !== value);
    }
    setFilters(next);
  };
  const handleClearAll = () => {
    setFilters(DEFAULT_FILTERS);
    toast.info('Semua filter dihapus');
  };
  return <div className="flex flex-col gap-3 sm:gap-4" data-id="element-765">
      {/* Breadcrumb + classification */}
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-766">
        <nav className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-767">
          <Link to="/dashboard" className="hover:text-foreground transition-colors" data-id="element-768">
            Beranda
          </Link>
          {group && <>
              <ChevronRight className="size-3" data-id="element-769" />
              <span data-id="element-770">{group}</span>
            </>}
          <ChevronRight className="size-3" data-id="element-771" />
          <span className="text-foreground" data-id="element-772">{title}</span>
        </nav>
        <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 font-mono text-[9px] uppercase text-red-700 border border-red-200" data-id="element-773">
          <Lock className="size-2.5" data-id="element-774" />
          {classification}
        </span>
      </div>

      {/* Title row */}
      <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-775">
        <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-776">
          {Icon && <div className="size-10 sm:size-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-777">
              <Icon className="size-5 text-primary" data-id="element-778" />
            </div>}
          <div className="min-w-0 flex-1" data-id="element-779">
            <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-foreground leading-tight" data-id="element-780">
              {title}
            </h1>
            {description && <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed" data-id="element-781">
                {description}
              </p>}
            <p className="font-mono text-[10px] text-muted-foreground mt-1.5" data-id="element-782">
              Diperbarui {lastUpdated} · Auto-refresh aktif
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0" data-id="element-783">
          {extraActions}
          <Button variant={activeCount > 0 ? 'default' : 'outline'} size="sm" className="px-2 sm:px-3 relative" onClick={handleFilterClick} data-id="element-784">
            <Filter className="size-3.5 sm:mr-1.5" data-id="element-785" />
            <span className="hidden sm:inline" data-id="element-786">Filter</span>
            {activeCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-foreground/20 text-[10px] font-bold" data-id="element-787">
                {activeCount}
              </span>}
          </Button>
          <Button variant="outline" size="sm" className="px-2 sm:px-3" onClick={onRefresh ?? defaultRefresh} data-id="element-788">
            <RefreshCw className="size-3.5 sm:mr-1.5" data-id="element-789" />
            <span className="hidden sm:inline" data-id="element-790">Refresh</span>
          </Button>
          <Button variant="default" size="sm" className="px-2 sm:px-3" onClick={onExport ?? defaultExport} data-id="element-791">
            <Download className="size-3.5 sm:mr-1.5" data-id="element-792" />
            <span className="hidden sm:inline" data-id="element-793">Ekspor</span>
          </Button>
        </div>
      </div>

      {/* Active filter chips */}
      {activeCount > 0 && <FilterChips filters={filters} onClear={handleClearChip} onClearAll={handleClearAll} data-id="element-794" />}

      <FilterSheet open={filterOpen} onOpenChange={setFilterOpen} value={filters} onApply={handleApply} pageTitle={title} data-id="element-795" />
    </div>;
}